import { Card, Text, Button, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useMemo, useRef } from 'react';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, toaster, useValidatedSorting, type ConfirmModalHandle, type ItemAction } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteMultipleSmsMessageTemplates } from '../../../api/hooks/useSmsMessageTemplates';
import smsMessageTemplatesService from '../../../api/services/smsMessageTemplatesService';
import { SmsMessageTemplateDrawer } from './MessageTemplateDrawer';
import { SmsMessagesService, useDrawer } from '@admin';
import { useNavigate } from 'react-router';

const columnHelper = createColumnHelper<SmsMessageTemplate>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function SmsMessageTemplates() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { openDrawer, closeDrawer } = useDrawer();
	const navigate = useNavigate();
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<SmsMessageTemplate>(),
		[],
	);

	const columns = [
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('sender', {
			header: t('general.sender'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.display({
			id: 'smsMessageTemplateID',
			cell: ({ row }) => {
				return (
					<Flex width={'100%'} justifyContent={'end'}>
						<Button
							data-cy="useTemplate"
							onClick={e => {
								e.stopPropagation();
								handleUseTemplate(row.original.smsMessageTemplateID);
							}}
							size="xs"
						>
							{t('general.send_message_using_template')}
						</Button>
					</Flex>
				);
			},
		}),
	];

	useValidatedSorting('smsMessageTemplates', columns);

	async function handleUseTemplate(id: number): Promise<void> {
		const response = await SmsMessagesService.createFromTemplate(id);
		navigate(`/messages/drafts/${response.data.smsMessage.smsMessageID}`);
	}

	const { mutate: deleteMultipleSmsMessageTemplates } = useDeleteMultipleSmsMessageTemplates();

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const selectedItemsActions: Array<ItemAction<SmsMessageTemplate>> = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					smsMessageTemplates: ids.map(id => {
						return {
							smsMessageTemplateID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_message_templates'),
					content: t('general.really_remove_message_templates'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleSmsMessageTemplates(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['smsMessageTemplates'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
				<Flex display={'flex'} flexDirection={'column'} gap={3} flex={1} maxWidth={'100%'}>
					<DataCollectionContextProvider
						dataKey="smsMessageTemplates"
						keyAttribute="smsMessageTemplateID"
						emptyDataText={t('general.no_sms_message_templates_found')}
						fetchFn={(...args) => smsMessageTemplatesService.getAllResources(...args)}
						selectedItemsActions={selectedItemsActions}
						initialItemsPerPage={25}
						columns={columns}
						defaultHiddenColumns={[]}
						isServerSide={false}
						defaultSort={{ id: 'timeCreated', desc: true }}
						onClickRow={row => {
							openDrawer({
								id: 'messageTemplateDrawer',
								content: (
									<SmsMessageTemplateDrawer closeDrawer={() => closeDrawer('messageTemplateDrawer')} smsMessageTemplateID={row.original.smsMessageTemplateID} />
								),
							});
						}}
					>
						<Flex gap={2} flexWrap={'wrap'}>
							<DataCollectionTableHeader />
							<Button
								data-cy="addMessageTemplate"
								minW="175px"
								alignSelf="start"
								onClick={() =>
									openDrawer({
										id: 'messageTemplateDrawer',
										content: <SmsMessageTemplateDrawer closeDrawer={() => closeDrawer('messageTemplateDrawer')} />,
									})
								}
							>
								{t('general.add_message_template')}
							</Button>
						</Flex>
						<DataCollectionTable />
						<Flex gap={2}>
							<DataCollectionTableFooter />
							<ConfirmModal ref={confirmRemoveRef} />
						</Flex>
					</DataCollectionContextProvider>
				</Flex>
			</Card.Body>
		</Card.Root>
	);
}
